import type { CustomObjectField as ApiCustomObjectField } from '@kizen/api/custom-object';
import type { EmployeeCustomObjectConfig } from '@kizen/api/employee';
import type { Role as ApiRole } from '@kizen/api/role';
import type {
  CreateTeamMemberData,
  SearchTeamMembersOptions,
  TeamMember,
} from '@kizen/api/team-member';
import type { PropsWithChildren } from 'react';
import type { Store } from 'store/types';
import type { FieldInputValue } from 'utility/getFieldInputValue';
import type { SortColumn } from 'utility/SortingHelpers';
import type { SettingsState } from '../../SettingsContext';
import type { EditingTeamMember, TeamPageProps } from './types';

import styled from '@emotion/styled';
import { updateEmployeeCustomObjectConfig } from '@kizen/api/employee';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'react-use';
import { layers } from 'app/spacing';
import { KizenTypography } from '__app/typography';
import Checkbox from '__components/Checkbox/Checkbox';
import Loader from 'components/Kizen/Loader';
import { TRow } from '__components/Kizen/Table';
import { HoverableRow } from '__components/Tables/Big/NewRowMenuCell';
import BigTableLayout from '__components/Layout/BigTableLayout';
import { PageSizing } from '__components/Layout/PageContentWidth';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import PageToolbar, {
  PageSearchInput,
  PageToolbarSection,
  PageToolbarTitle,
} from '__components/Layout/PageToolbar';
import BasicModal from '__components/Modals/presets/BasicModal';
import { DEFAULT_TEMPLATE_ID } from 'components/Modals/ApplyTemplateModal';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import ConfirmNavigationModal from '__components/Modals/presets/ConfirmNavigation';
import BigTable from '__components/Tables/Big';
import TablePagination from '__components/Tables/Big/TablePagination';
import { toastVariant, useToast } from '__components/ToastProvider';
import { EmployeeHistoryModal } from '__pages/Settings/pages/Team/HistoryModal';
import { KeyBoardContext } from '__hooks/keyboardEventHandler/useKeyBoardContext';
import { useKeyListenersInline } from '__hooks/keyboardEventHandler/useKeyListenersInline';
import { getErrorMessage } from 'hooks/useErrors';
import useFormValidation from 'hooks/useFormValidation';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { customObjectFieldsQuery } from 'query/custom-object';
import { employeeCustomObjectConfigQuery } from 'query/employee';
import { rolesQuery } from 'query/role';
import { searchTeamMembersQuery } from 'query/team-member';
import AxiosService, { getOriginalError } from 'services/AxiosService';
import TeamMemberService from 'services/TeamMemberService';
import AuthenticationService from 'services/AuthenticationService';
import {
  camelToSnakeCase,
  camelToSnakeCaseKeys,
  snakeToCamelCaseKeys,
} from '__services/helpers';
import {
  refreshTeamMemberSuccess,
  serviceRefreshAccessStart,
} from 'store/authentication/authenticationAction';
import { getChosenBusiness } from 'store/authentication/selectors';
import {
  getBusinessTeamMembersObject,
  getUserProfile,
} from 'store/authentication/selectors';
import {
  FilterSets,
  useFilterSetsContext,
} from 'ts-components/filters/FilterSets';
import { DEFAULT_INPUT_DELAY } from 'utility/config';
import { FIELD_TYPES } from 'utility/constants';
import { getFieldInputValue } from 'utility/getFieldInputValue';
import { getOrderingParam, getSortValue } from 'utility/SortingHelpers';
import { useSettingsContext } from '../../SettingsContext';
import { ApplyTemplateModal } from './ApplyTemplateModal';
import { APPLY_TEMPLATES_TYPES } from './constants';
import {
  getTeamColumns,
  getTeamCustomFieldColumns,
  keyListenerIds,
} from './columns';
import TeamMemberForm from './TeamMemberForm';
import { TeamMemberColumnsModal } from './TeamMemberColumnsModal';
import UploadTeamMembersModal from './UploadTeamMembersModal';
import { ExportToCSVButton, NewTeamMemberButton } from './styles';
import { openMenuAboveMeta } from './util';
import { UI_THEMES } from '@kizen/kds/Colors';

type CustomObjectField = SnakeToCamelCase<ApiCustomObjectField>;

type Role = SnakeToCamelCase<ApiRole>;

type NewTeamMemberOption =
  | { value: 'new_team_member'; label: string }
  | { value: 'upload_team_members'; label: string };

type Action =
  | 'apply-columns'
  | 'apply-quick-filters'
  | 'apply-toolbar'
  | 'delete'
  | 'edit'
  | 'history'
  | 'reset-password'
  | 'resend-verification';

type TemplateModal = {
  data: SnakeToCamelCase<TeamMember>;
  templateType: 'columns' | 'quickFilters' | 'toolbars';
};

type ExistingTeamMember = EditingTeamMember & { id: string };

const StyledBigTableLayout = styled(BigTableLayout)`
  min-height: auto;

  && > div {
    padding: 0;
  }

  && > div:last-child > div:last-child {
    margin-bottom: 25px;
  }
`;

const ConfirmDeleteCheckbox = styled(Checkbox)`
  margin: 0;
  width: max-content;

  span {
    max-width: unset;
  }
`;

const ConfirmDeleteContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: left;
`;

const TEAM_MEMBERS_QUERY_KEY = searchTeamMembersQuery().queryKey;

const DEFAULT_COLUMNS = [
  'full_name',
  'email',
  'roles',
  'permission_groups',
  'annual_sales_goal',
  'start_date',
  'last_login',
  'verified',
];

const teamMemberValuesToApiValues = (
  data: EditingTeamMember & { fields: Record<string, FieldInputValue> },
  fieldsByIds: Record<string, CustomObjectField>
): SnakeToCamelCase<CreateTeamMemberData> => {
  const {
    permissionGroups,
    roles,
    annualSalesGoal,
    phone,
    toolbarTemplateId,
    fields: _fields,
    errors: _errors,
    ...rest
  } = data;

  const fields = Object.entries(data.fields).reduce(
    (acc: SnakeToCamelCase<CreateTeamMemberData['fields']>, [id, v]) => {
      const value = v as any;
      const field = fieldsByIds[id];

      if (!field) {
        return acc;
      }

      if (
        [FIELD_TYPES.DynamicTags.type, FIELD_TYPES.Files.type].includes(
          field.fieldType
        )
      ) {
        acc.push({
          id,
          value: (value || []).map((x: any) => x.id).filter(Boolean),
        });
      } else if (field.fieldType === FIELD_TYPES.TeamSelector.type) {
        acc.push({ id, value: value?.id ?? null });
      } else if (field.fieldType === FIELD_TYPES.Checkboxes.type) {
        acc.push({
          id,
          value: (value || []).map((x: any) => x.value ?? x).filter(Boolean),
        });
      } else {
        acc.push({ id, value });
      }
      return acc;
    },
    []
  );

  const result: SnakeToCamelCase<CreateTeamMemberData> = {
    ...rest,
    fields,
    phone: phone || '',
    roles: roles.map((role) => role.id),
    permissionGroups: permissionGroups.map((pg) => pg.id),
    annualSalesGoal: annualSalesGoal || 0,
  };

  if (toolbarTemplateId !== DEFAULT_TEMPLATE_ID) {
    result.toolbarTemplateId = toolbarTemplateId;
  }

  return result;
};

const getMenuOptions = (data: {
  access: TeamPageProps['access'];
  t: (x: string) => string;
  teamMember: SnakeToCamelCase<TeamMember>;
  userId: string;
}) => {
  const { access, t, teamMember, userId } = data;
  const result = [];

  if (!teamMember.verified) {
    result.push({
      value: 'resend-verification',
      label: t('Resend Verification'),
    });
  }

  result.push({ value: 'edit', label: t('Edit') });

  if (teamMember.verified) {
    result.push({
      value: 'reset-password',
      label: t('Reset Password'),
    });
  }

  if (access.teamMembers.edit) {
    result.push(
      {
        value: 'apply-toolbar',
        label: t('Apply Toolbar'),
      },
      {
        value: 'apply-columns',
        label: t('Apply Column Template'),
      },
      {
        value: 'apply-quick-filters',
        label: t('Apply Quick Filter Template'),
      }
    );
  }

  result.push({
    value: 'history',
    label: t('View History'),
  });

  if (teamMember.user !== userId && access.teamMembers.remove) {
    result.push({
      value: 'delete',
      label: t('Archive'),
    });
  }

  return result;
};

const newTeamMember = (roles: Role[]): EditingTeamMember => {
  const defaultRoles = roles.reduce<EditingTeamMember['roles']>((acc, role) => {
    if (role.defaultForNewUsers) {
      acc.push(role);
    }
    return acc;
  }, []);

  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    startDate: new Date().toISOString(),
    roles: defaultRoles,
    permissionGroups: [],
    trackAnnualSalesGoal: false,
    toolbarTemplateId: DEFAULT_TEMPLATE_ID,
    annualSalesGoal: null,
    theme: UI_THEMES.LIGHT,
  };
};

const toEditingTeamMember = (
  tm: SnakeToCamelCase<TeamMember>
): EditingTeamMember => {
  let annualSalesGoal =
    tm.annualSalesGoal !== null && tm.annualSalesGoal !== undefined
      ? parseFloat(tm.annualSalesGoal)
      : tm.annualSalesGoal;

  if (!annualSalesGoal || Number.isNaN(annualSalesGoal)) {
    annualSalesGoal = null;
  }

  return {
    id: tm.id,
    firstName: tm.firstName ?? '',
    lastName: tm.lastName ?? '',
    email: tm.email ?? '',
    phone: tm.phone ?? '',
    startDate: tm.startDate ?? new Date().toISOString(),
    roles: tm.roles,
    permissionGroups: tm.permissionGroups,
    annualSalesGoal,
    trackAnnualSalesGoal: tm.trackAnnualSalesGoal ?? false,
    toolbarTemplateId: DEFAULT_TEMPLATE_ID,
    theme: tm.theme ?? UI_THEMES.LIGHT,
  };
};

const isExistingTeamMember = (
  tm: EditingTeamMember
): tm is ExistingTeamMember => {
  return Boolean(tm.id);
};

const Page = ({ access }: TeamPageProps) => {
  const preReleaseFeature = usePreReleaseFeatures();
  const [showToast] = useToast();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const qc = useQueryClient();

  const chosenBusiness = useSelector(getChosenBusiness);
  const { user: userId } = useSelector(getUserProfile);
  const teamMembersObject = useSelector(getBusinessTeamMembersObject);
  const currentTeamMemberId = useSelector(
    (state: Store) => state.authentication.teamMember?.id
  );
  const employeeObject = useSelector((s: Store) => {
    return s.authentication.chosenBusiness.employee_object;
  });

  const ctx = useSettingsContext();
  const { search, sort, setSearch, setSort } = ctx.teamMembers;

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [perPage, setPerPage] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const [deleteTeamMember, setDeleteTeamMember] =
    useState<SnakeToCamelCase<TeamMember> | null>(null);
  const [deleteChecked, setDeleteChecked] = useState(false);
  const [confirmFormDiscard, setConfirmFormDiscard] = useState(false);
  const [templateModal, setTemplateModal] = useState<TemplateModal | null>();
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [teamMember, setTeamMember] = useState<EditingTeamMember | null>(null);
  const [fieldsValues, setFieldValues] = useState<
    Record<string, FieldInputValue>
  >({});
  const [uploadTeamMember, setUploadTeamMember] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [showEditColumns, setShowEditColumns] = useState(false);

  useDebounce(() => setDebouncedSearch(search), DEFAULT_INPUT_DELAY, [search]);

  const { config, group, isLoading: loadingFilters } = useFilterSetsContext();

  const columnsQueryOptions = employeeCustomObjectConfigQuery(
    employeeObject.id,
    {
      staleTime: Infinity,
      select: (data) => {
        return data.column_config;
      },
    }
  );

  const columnsQuery = useQuery(columnsQueryOptions);

  const { column_order = DEFAULT_COLUMNS } = columnsQuery.data ?? {};

  const { data: teamMemberFields = [], isLoading: fieldsAreLoading } = useQuery(
    customObjectFieldsQuery(teamMembersObject.id, {
      select: (data) => {
        const camel = snakeToCamelCaseKeys(data);
        const visible = camel.filter((field) => !field.isHidden);
        return visible;
      },
    })
  );

  const { data: roles = [], isLoading: rolesIsLoading } = useQuery(
    rolesQuery({
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      select: (data) => {
        return snakeToCamelCaseKeys(data).sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      },
    })
  );

  const teamQueryOptions = useMemo(() => {
    const data: Pick<SearchTeamMembersOptions, 'params' | 'body'> = {
      params: {
        search: debouncedSearch,
        ordering: sort,
        page_size: perPage,
        page: pageNumber,
      },
    };

    if (config) {
      data.body = {
        and: config.and,
        query: config.query,
      };
    } else if (group) {
      data.params!.group_id = group.id;
    }

    const options = searchTeamMembersQuery({
      ...data,
      enabled: !loadingFilters,
      placeholderData: keepPreviousData,
      select: (data) => {
        const { results, ...rest } = data;
        const mapped = {
          ...snakeToCamelCaseKeys(rest),
          results: results.map((tm, index, arr) => {
            return {
              ...snakeToCamelCaseKeys(tm),
              access: { view: true, edit: true },
              meta: openMenuAboveMeta({ index, total: arr.length }),
            };
          }),
        };

        const snakeCaseLookup = data.results.reduce<Record<string, TeamMember>>(
          (acc, tm) => {
            acc[tm.id] = tm;
            return acc;
          },
          {}
        );

        return { ...mapped, snakeCaseLookup };
      },
    });

    return options;
  }, [
    debouncedSearch,
    sort,
    perPage,
    pageNumber,
    loadingFilters,
    config,
    group,
  ]);

  const teamMembersQuery = useQuery(teamQueryOptions);

  const handleChangeSearch = useCallback(
    (value: string) => {
      setSearch(value);
      setPageNumber(1);
    },
    [setSearch]
  );

  const handleUpdateTeamMember = useCallback(
    async (
      member: SnakeToCamelCase<TeamMember> | ExistingTeamMember,
      patch: SnakeToCamelCase<CreateTeamMemberData>
    ) => {
      let updated;

      try {
        updated = await TeamMemberService.update(member, patch);
      } catch (error) {
        const message = getOriginalError(error);
        if (message?.errors?.[0])
          showToast({
            variant: toastVariant.FAILURE,
            delay: 3000,
            message: message.errors?.[0],
          });
        throw error;
      } finally {
        if (updated?.id === currentTeamMemberId) {
          dispatch(
            refreshTeamMemberSuccess({
              teamMember: updated,
            })
          );
        }
        // In the background, refresh access any time settings change that might impact the current user's perms
        dispatch(serviceRefreshAccessStart() as any);
        qc.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY });
      }
    },
    [currentTeamMemberId, dispatch, qc, showToast]
  );

  const handleUpdateCustomFieldValue = useCallback(
    (data: {
      column: { id: string; label: string };
      entityId: string;
      values: { field: any; value: any }[];
    }) => {
      const { entityId, values } = data;
      const teamMember = teamMembersQuery.data?.results.find(
        (tm) => tm.id === entityId
      );

      if (teamMember) {
        const fields = values.map((v) => {
          return { id: v.field.id, value: v.value };
        });

        handleUpdateTeamMember(teamMember, { fields });
      }
    },
    [handleUpdateTeamMember, teamMembersQuery.data]
  );

  const {
    validateFormState,
    validationProps,
    handleInputChange,
    resetValidationState,
    setValidationState,
    firstFieldWithErrorRef,
  } = useFormValidation({
    setFormDirty,
    formState: teamMember,
    setFormState: setTeamMember,
  });

  const {
    validateFormState: validateFieldsFormState,
    validationProps: fieldsValidationProps,
    handleInputChange: handleFieldsInputChange,
    resetValidationState: resetFieldsValidationState,
    setValidationState: setFieldsValidationState,
    firstFieldWithErrorRef: firstFieldsFieldWithErrorRef,
    validationState,
  } = useFormValidation({
    setFormDirty,
    formState: fieldsValues,
    setFormState: setFieldValues,
  });

  const fieldsByIds = useMemo(() => {
    return teamMemberFields.reduce(
      (acc: Record<string, CustomObjectField>, field: CustomObjectField) => {
        acc[field.id] = field;
        return acc;
      },
      {}
    );
  }, [teamMemberFields]);

  const fieldValues = useMemo(() => {
    const result = teamMembersQuery.data?.results.reduce<
      Record<string, Record<string, any>>
    >((acc, tm) => {
      const values = Object.values(tm.fields).reduce<Record<string, any>>(
        (acc, fv) => {
          const field = fieldsByIds[fv.id];
          const entity = teamMembersQuery.data?.snakeCaseLookup[tm.id];
          const value = getFieldInputValue(field, entity);
          acc[fv.id] = value;
          return acc;
        },
        {}
      );
      acc[tm.id] = values;
      return acc;
    }, {});

    return result ?? {};
  }, [teamMembersQuery.data, fieldsByIds]);

  const teamColumns = useMemo(() => {
    const customColumns = getTeamCustomFieldColumns({
      onSubmit: handleUpdateCustomFieldValue,
      model: employeeObject,
      fields: teamMemberFields,
    });

    const defaultColumns = getTeamColumns(
      {
        onEditColumns: () => setShowEditColumns(true),
        onUpdateField: (
          member: SnakeToCamelCase<TeamMember>,
          patch: SnakeToCamelCase<CreateTeamMemberData>
        ) => {
          handleUpdateTeamMember(member, patch);
        },
        onSelectAction: async (
          action: { value: Action },
          tm: SnakeToCamelCase<TeamMember>
        ) => {
          switch (action.value) {
            case 'delete':
              setDeleteTeamMember(tm);
              break;
            case 'resend-verification':
              await TeamMemberService.resendVerification(tm.id);
              showToast({
                variant: toastVariant.SUCCESS,
                delay: 3000,
                message: t(
                  'The verification email has been sent successfully.'
                ),
              });
              break;
            case 'reset-password':
              await AuthenticationService.requestPasswordReset(tm.email);
              break;
            case 'edit': {
              const valuesByIds = Object.values(tm.fields).reduce<
                Record<string, FieldInputValue>
              >((acc, field) => {
                const fields = camelToSnakeCaseKeys(tm.fields) as any;
                const value = getFieldInputValue(
                  { id: field.id, name: field.name },
                  { fields, id: tm.id }
                );

                if (value) {
                  acc[field.id] = value;
                }

                return acc;
              }, {});

              setTeamMember(toEditingTeamMember(tm));
              setFieldValues(valuesByIds);
              break;
            }
            case 'apply-toolbar':
              setTemplateModal({
                templateType: 'toolbars',
                data: tm,
              });
              break;
            case 'apply-columns':
              setTemplateModal({
                templateType: 'columns',
                data: tm,
              });
              break;
            case 'apply-quick-filters':
              setTemplateModal({
                templateType: 'quickFilters',
                data: tm,
              });
              break;
            case 'history':
              setHistoryId(tm.id);
              break;
            default:
              break;
          }
        },
      },
      t,
      preReleaseFeature,
      showToast,
      access,
      chosenBusiness
    );

    const columns = defaultColumns.concat(customColumns);
    const spacer = defaultColumns[0];
    const actions = defaultColumns.at(-1);
    const result = [spacer];

    for (const name of column_order) {
      const column = columns.find((col) => name === camelToSnakeCase(col.id));

      if (column) {
        result.push(column);
      }
    }

    result.push(actions);

    return result;
  }, [
    t,
    preReleaseFeature,
    showToast,
    access,
    chosenBusiness,
    handleUpdateTeamMember,
    handleUpdateCustomFieldValue,
    column_order,
    teamMemberFields,
    employeeObject,
  ]);

  const teamHeadData = useMemo(
    () => ({
      meta: {
        sort: sort ? getSortValue(sort) : null,
        onSort: (
          column: SortColumn<SettingsState['teamMembers']['sort']>,
          direction: 'asc' | 'desc'
        ) => {
          const sort = getOrderingParam({ column, direction });
          setSort(sort);
        },
      },
    }),
    [sort, setSort]
  );

  const handleHideForm = useCallback(() => {
    resetValidationState();
    resetFieldsValidationState();
    setTeamMember(null);
    setFieldValues({});
  }, [resetValidationState, resetFieldsValidationState]);

  const onClickExportTeam = async () => {
    try {
      await TeamMemberService.exportTeam({}, {});
      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          'Team Members were queued for export successfully. You will be emailed a download link once complete.'
        ),
      });
    } catch (err) {
      monitoringExceptionHelper(err);
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'Failed to export Team Members. Please try again or contact Kizen support.'
        ),
      });
    }
  };

  const fieldListeners = useMemo(() => {
    return (
      teamMembersQuery.data?.results.flatMap((tm) => {
        return teamColumns.reduce((acc, column) => {
          const fieldId = column.bodyCell?.field?.id;

          if (fieldId) {
            acc.push({ id: `${fieldId}-${tm.id}` });
          } else if (column.id in keyListenerIds) {
            const id = (keyListenerIds as any)[column.id]?.(tm);
            acc.push({ id });
          }

          return acc;
        }, []);
      }) ?? []
    );
  }, [teamMembersQuery.data, teamColumns]);

  const { assignFieldHandle, getKeyListenersProps } = useKeyListenersInline(
    fieldListeners,
    {},
    () => true,
    false
  );

  if (
    loadingFilters ||
    teamMembersQuery.isLoading ||
    rolesIsLoading ||
    fieldsAreLoading
  ) {
    return <Loader loading />;
  }

  return (
    <PageSizing>
      {deleteTeamMember && (
        <ConfirmDeletionModal
          show
          size="medium"
          typeOfContent="text"
          disabled={!deleteChecked}
          defaultLeftBtnText={t('Cancel')}
          onHide={() => {
            setDeleteTeamMember(null);
            setDeleteChecked(false);
          }}
          onConfirm={async () => {
            try {
              await TeamMemberService.delete(deleteTeamMember.id);
            } catch (err: any) {
              const { data } = err.response;

              if (data.statusCode === 400) {
                showToast({
                  variant: toastVariant.FAILURE,
                  delay: 3000,
                  message: data.errors[0],
                });
              }
            } finally {
              setDeleteTeamMember(null);
              setDeleteChecked(false);
              qc.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY });
            }
          }}
        >
          <ConfirmDeleteContent>
            <div>
              <KizenTypography>
                <KizenTypography as="span" size="micro">
                  {t('This will permanently archive the Team Member')}{' '}
                </KizenTypography>
                <KizenTypography as="span" size="micro" weight="bold">
                  {`${deleteTeamMember.fullName} (${deleteTeamMember.email}).`}
                </KizenTypography>
              </KizenTypography>
              <KizenTypography>
                {t('Any Records assigned to them will be unassigned.')}
              </KizenTypography>
              <KizenTypography>
                {t(
                  'Their access to this business ({{ businessName }}) will be revoked immediately.',
                  { businessName: chosenBusiness.name }
                )}
              </KizenTypography>
            </div>
            <ConfirmDeleteCheckbox
              checked={deleteChecked}
              label={t('I understand this action is not reversible')}
              onChange={() => setDeleteChecked((x) => !x)}
            />
          </ConfirmDeleteContent>
        </ConfirmDeletionModal>
      )}
      <ConfirmNavigationModal
        when={formDirty}
        show={confirmFormDiscard}
        onHide={() => {
          setConfirmFormDiscard(false);
        }}
        onConfirm={() => {
          handleHideForm();
          setConfirmFormDiscard(false);
          setFormDirty(false);
        }}
      />

      {teamMember && (
        <BasicModal
          show
          heading={`${teamMember.id ? t('Edit') : t('Add')} ${t('Team Member')}`}
          buttonText={t('Save')}
          leftBtn={null}
          enforceFocus={false}
          onHide={() => {
            if (formDirty) {
              setConfirmFormDiscard(true);
            } else {
              handleHideForm();
            }
          }}
          onConfirm={async () => {
            if (!validateFormState(t) || !validateFieldsFormState(t)) {
              return;
            }

            const payload = teamMemberValuesToApiValues(
              { ...teamMember, fields: fieldsValues },
              fieldsByIds
            );

            try {
              if (isExistingTeamMember(teamMember)) {
                await handleUpdateTeamMember(teamMember, payload);
              } else {
                await TeamMemberService.add(payload);
              }
            } catch (err) {
              const orig = getOriginalError(err);
              if (orig?.errors?.fields) {
                const fieldErrors = orig.errors.fields.reduce(
                  (acc: any, fieldError: any, i: number) => {
                    const message = getErrorMessage(fieldError);
                    if (message) {
                      acc[payload.fields[i].id] = message;
                    }
                    return acc;
                  },
                  {}
                );
                firstFieldsFieldWithErrorRef.current = Object.keys(
                  fieldErrors
                )[0] as any;
                setFieldsValidationState(fieldErrors);
              } else if (orig?.errors) {
                firstFieldWithErrorRef.current = Object.keys(
                  orig.errors || {}
                )[0] as any;
                setValidationState(orig.errors || null);
              } else {
                const message = getErrorMessage(orig);

                if (message) {
                  showToast({
                    variant: toastVariant.FAILURE,
                    delay: 3000,
                    message,
                  });
                }
              }
              return;
            } finally {
              qc.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY });
            }

            setTeamMember(null);
            setFormDirty(false);
            setFieldValues({});
            resetValidationState();
            resetFieldsValidationState();
          }}
        >
          <TeamMemberForm
            isEdit={Boolean(teamMember.id)}
            roles={roles}
            values={teamMember}
            fieldsValues={fieldsValues}
            validationProps={validationProps}
            fieldsValidationProps={(fieldId: string) => {
              const { error, ...rest } = fieldsValidationProps(fieldId);
              return { ...rest, disableOldValidation: false };
            }}
            disableEmail={Boolean(teamMember.id)}
            onChange={handleInputChange}
            onFieldsChange={handleFieldsInputChange}
            teamMemberFields={teamMemberFields}
            fieldErrors={validationState}
          />
        </BasicModal>
      )}
      {uploadTeamMember && (
        <UploadTeamMembersModal onHide={() => setUploadTeamMember(false)} />
      )}
      {access.teamMembers.edit ? (
        <>
          <ContentWidth>
            <PageToolbar>
              <PageToolbarSection>
                <FilterSets.GroupSelector />
                <FilterSets.IconButton />
                <PageSearchInput
                  isClearable
                  maxWidth={200}
                  loading={teamMembersQuery.isFetching}
                  placeholder={t('Find Team Members')}
                  value={search}
                  onChange={handleChangeSearch}
                />
              </PageToolbarSection>
              <PageToolbarTitle
                count={teamMembersQuery.data?.count}
                single={t('Team Member')}
                plural={t('Team Members')}
              />
              <PageToolbarSection>
                <ExportToCSVButton onClick={onClickExportTeam} color="blue">
                  {t('Export to CSV')}
                </ExportToCSVButton>
                <NewTeamMemberButton
                  onClick={() => {
                    setTeamMember(newTeamMember(roles));
                  }}
                  onChange={({ value }: NewTeamMemberOption) => {
                    if (value === 'new_team_member') {
                      setTeamMember(newTeamMember(roles));
                    }
                    if (value === 'upload_team_members') {
                      if (chosenBusiness?.is_trial) {
                        showToast({
                          variant: toastVariant.FAILURE,
                          message: t(
                            'Team Member upload is not available during trial period.'
                          ),
                        });
                      } else {
                        setUploadTeamMember(true);
                      }
                    }
                  }}
                  options={[
                    {
                      value: 'new_team_member',
                      label: t('New Team Member'),
                    },
                    {
                      value: 'upload_team_members',
                      label: t('Upload Team Members'),
                    },
                  ]}
                >
                  {t('New Team Member')}
                </NewTeamMemberButton>
              </PageToolbarSection>
            </PageToolbar>
            <FilterSets.Builder />
          </ContentWidth>
          <KeyBoardContext.Provider
            value={{ assignFieldHandle, getKeyListenersProps }}
          >
            <StyledBigTableLayout
              hideCurtainWrapper={true}
              pagination={
                <TablePagination
                  page={pageNumber}
                  perPage={perPage}
                  totalCount={teamMembersQuery.data?.count}
                  onChangePage={setPageNumber}
                  onChangePerPage={setPerPage}
                />
              }
            >
              <BigTable
                headerPosition="unset"
                columns={teamColumns}
                head={<TRow head columns={teamColumns} data={teamHeadData} />}
                zIndex={layers.content(0, 0)} // needs to at zero - {todo} reafctor big table so the z-indexes work with out hacks
                scrolledToTable={false}
              >
                {teamMembersQuery.data?.results?.map((teamMember) => {
                  return (
                    <HoverableRow
                      key={teamMember.id}
                      columns={teamColumns}
                      data={teamMember}
                      fieldValues={fieldValues[teamMember.id]}
                      fixWidthStyle={{ padding: '30px' }}
                      menuCellOptions={getMenuOptions({
                        access,
                        teamMember,
                        t,
                        userId,
                      })}
                    />
                  );
                })}
              </BigTable>
            </StyledBigTableLayout>
          </KeyBoardContext.Provider>
        </>
      ) : null}
      {templateModal ? (
        <ApplyTemplateModal
          show
          applyTo={APPLY_TEMPLATES_TYPES.teamMembers}
          data={templateModal.data}
          templateType={templateModal.templateType}
          onHide={() => setTemplateModal(null)}
        />
      ) : null}
      {historyId && (
        <EmployeeHistoryModal
          id={historyId}
          onHide={() => setHistoryId(null)}
        />
      )}
      {showEditColumns && (
        <TeamMemberColumnsModal
          columns={column_order}
          onHide={() => setShowEditColumns(false)}
          onConfirm={async (fields) => {
            const names = fields.map((field) => field.name);
            const config = { column_config: { column_order: names } };

            qc.setQueryData(columnsQueryOptions.queryKey, config);

            await updateEmployeeCustomObjectConfig(
              AxiosService,
              employeeObject.id,
              config
            );
          }}
        />
      )}
    </PageSizing>
  );
};

const TeamMemberFilterSets = ({
  access,
  children,
}: PropsWithChildren<TeamPageProps>) => {
  const qc = useQueryClient();
  const employeeObjectId = useSelector((s: Store) => {
    return s.authentication.chosenBusiness.employee_object.id;
  });

  const queryOptions = employeeCustomObjectConfigQuery(employeeObjectId, {
    staleTime: Infinity,
  });
  const queryKey = queryOptions.queryKey;
  const query = useQuery(queryOptions);

  const { record_list_page_config } = query.data ?? {};
  const { filter_object, group } = record_list_page_config ?? {};

  if (query.isLoading) {
    return <Loader loading />;
  }

  if (!access.teamMembers.edit) {
    return <>{children}</>;
  }

  const updatePageConfig = async (config: EmployeeCustomObjectConfig) => {
    await updateEmployeeCustomObjectConfig(
      AxiosService,
      employeeObjectId,
      config
    );

    qc.setQueryData(queryKey, config);
  };

  return (
    <FilterSets.Provider
      instance="record_overview_team_member"
      objectId={employeeObjectId}
      config={filter_object}
      groupId={group}
      onApplyFilters={async (data) => {
        const config = {
          record_list_page_config: {
            ...record_list_page_config,
            filter_object: data.config,
          },
        };

        if (!data.group) {
          await updatePageConfig(config);
        } else {
          qc.setQueryData(queryKey, config);
        }
      }}
      onClearFilter={async () => {
        await updatePageConfig({
          record_list_page_config: {
            ...record_list_page_config,
            filter_object: {},
            group: null,
          },
        });
      }}
      onDeleteGroup={async () => {
        await updatePageConfig({
          record_list_page_config: {
            ...record_list_page_config,
            group: null,
          },
        });
      }}
      onGroupChange={async (group) => {
        await updatePageConfig({
          record_list_page_config: {
            ...record_list_page_config,
            filter_object: {},
            group: group ? group.id : null,
          },
        });
      }}
      onSaveGroup={async (data) => {
        await updatePageConfig({
          record_list_page_config: {
            ...record_list_page_config,
            group: data.id,
            filter_object: {},
          },
        });
      }}
    >
      {children}
    </FilterSets.Provider>
  );
};

export default function TeamPage(props: TeamPageProps) {
  return (
    <TeamMemberFilterSets {...props}>
      <Page {...props} />
    </TeamMemberFilterSets>
  );
}
